import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { Boards } from '@air/api';
import { type Board } from '@air/api/types';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { editBoardSuccessAction } from '~/store/centralizedBoard/actions';
import { useUpdateBoardInAllLists } from '~/utils/mutateUtils/AllBoards';

export type UsePrivateBoardsDeleteBoardCoverMutationParams = Parameters<typeof Boards.deleteCoverImage>[0] & {
  board: Board;
};

export const usePrivateBoardsDeleteBoardCoverMutation = () => {
  const dispatch = useDispatch();
  const { updateBoardInAllLists } = useUpdateBoardInAllLists();

  return useMutation({
    mutationFn: async (params: UsePrivateBoardsDeleteBoardCoverMutationParams) => {
      return callAirApi(() =>
        Boards.deleteCoverImage({
          boardId: params.boardId,
          workspaceId: params.workspaceId,
        }),
      );
    },

    onMutate: ({ board }) => {
      // @TODO - We should update this mutation hook to not rely on passing `board` as a parameter <https://linear.app/air/issue/COR-1406/remove-board-as-a-required-parameters-from-board-cover-mutation-hooks>
      if (board) {
        const updatedBoard: Board = {
          ...board,
          coverImage: undefined,
        };

        updateBoardInAllLists(updatedBoard);
        dispatch(editBoardSuccessAction({ board: updatedBoard }));
      }
    },

    onError: (error, variables) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to delete board cover',
        metadata: {
          key: 'data',
          data: {
            boardId: variables.boardId,
            workspaceId: variables.workspaceId,
          },
        },
      });
    },
  });
};
